import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Typography, Card, CardContent, Tabs, Tab, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Chip, CircularProgress,
  ToggleButton, ToggleButtonGroup, Divider, Button,
} from '@mui/material';
import toast from 'react-hot-toast';
import { trading, bot } from '../services/api';
import dayjs from '../utils/dayjs';

const fmt = (v) => `₹${parseFloat(v || 0).toFixed(2)}`;

function PositionsTable({ positions, loading, onClosePosition, actionLoadingId, showAction = false }) {
  if (loading) return <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress /></Box>;
  if (!positions.length) return <Box sx={{ textAlign: 'center', py: 3 }}><Typography color="text.secondary">No positions</Typography></Box>;

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ '& th': { fontWeight: 600, bgcolor: '#f5f5f5' } }}>
            <TableCell>Symbol</TableCell><TableCell>Type</TableCell><TableCell>Strike</TableCell>
            <TableCell>Expiry</TableCell><TableCell align="right">Qty</TableCell>
            <TableCell align="right">Entry</TableCell><TableCell align="right">Current</TableCell>
            <TableCell align="right">SL</TableCell><TableCell align="right">Target</TableCell>
            <TableCell align="right">Unrealized P&L</TableCell><TableCell align="center">Status</TableCell>
            {showAction && <TableCell align="center">Action</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {positions.map(pos => {
            const pnl = parseFloat(pos.unrealized_pnl || 0);
            const entry = parseFloat(pos.entry_price || 0);
            const cur = parseFloat(pos.current_price || entry || 0);
            const pnlPct = entry > 0 ? ((cur - entry) / entry) * 100 : 0;
            return (
              <TableRow key={pos.id} hover>
                <TableCell sx={{ fontWeight: 600 }}>
                  {pos.symbol}
                  {pos.expiry_date && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {dayjs(pos.expiry_date).format('D MMM YYYY').toUpperCase()}
                    </Typography>
                  )}
                </TableCell>
                <TableCell><Chip size="small" label={pos.instrument_type} color={pos.instrument_type === 'CE' ? 'success' : 'error'} /></TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{pos.strike_price ? Math.round(parseFloat(pos.strike_price)) : '—'}</TableCell>
                <TableCell>{pos.expiry_date ? dayjs(pos.expiry_date).format('DD/MM/YY') : '—'}</TableCell>
                <TableCell align="right">{pos.quantity}</TableCell>
                <TableCell align="right">{fmt(pos.entry_price)}</TableCell>
                <TableCell align="right">{fmt(pos.current_price)}</TableCell>
                <TableCell align="right" sx={{ color: '#c62828' }}>{fmt(pos.stop_loss)}</TableCell>
                <TableCell align="right" sx={{ color: '#2e7d32' }}>{fmt(pos.target)}</TableCell>
                <TableCell align="right" sx={{ color: pnl >= 0 ? '#2e7d32' : '#c62828', fontWeight: 700 }}>
                  {pnl >= 0 ? '+' : ''}{fmt(pnl)} ({pnlPct >= 0 ? '+' : ''}{pnlPct.toFixed(2)}%)
                </TableCell>
                <TableCell align="center"><Chip size="small" label={pos.status} color={pos.status === 'OPEN' ? 'primary' : 'default'} /></TableCell>
                {showAction && (
                  <TableCell align="center">
                    {pos.status === 'OPEN' && (
                      <Button
                        size="small"
                        variant="contained"
                        color="error"
                        disabled={actionLoadingId === pos.id}
                        onClick={() => onClosePosition(pos)}
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          py: 0.3,
                          px: 1.2,
                          minWidth: 50,
                          textTransform: 'none',
                          boxShadow: 'none',
                          '&:hover': { bgcolor: '#b91c1c', boxShadow: 'none' },
                        }}
                      >
                        {actionLoadingId === pos.id ? <CircularProgress size={13} color="inherit" /> : 'Sell'}
                      </Button>
                    )}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function TradingPage() {
  const [tab, setTab] = useState(0);
  const [mode, setMode] = useState('PAPER');
  const [positions, setPositions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState(null);

  // Set default mode from bot status on mount
  useEffect(() => {
    bot.status().then(res => {
      const tradeMode = res.data?.data?.trade_mode || 'PAPER';
      setMode(tradeMode);
    }).catch(() => {});
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      if (mode === 'PAPER') {
        const [pos, ord, acc] = await Promise.all([
          trading.paperPositions(),
          trading.paperOrders({ limit: 30 }),
          trading.paperAccount(),
        ]);
        setPositions(pos.data.data);
        setOrders(ord.data.data);
        setAccount(acc.data.data);
      } else {
        const [pos, ord] = await Promise.all([
          trading.livePositions(),
          trading.liveOrders({ limit: 30 }),
        ]);
        setPositions(pos.data.data);
        setOrders(ord.data.data);
        setAccount(null);
      }
    } catch { /* handled */ } finally { setLoading(false); }
  };

  useEffect(() => {
    loadData();
    const t = setInterval(loadData, 15000);
    return () => clearInterval(t);
  }, [mode]);

  const handleClosePosition = async (pos) => {
    if (!window.confirm(`Are you sure you want to sell/close ${pos.symbol} ${pos.instrument_type} (${pos.quantity} lot)?`)) return;
    setActionLoadingId(pos.id);
    try {
      const res = await trading.closePosition({ id: pos.id, mode });
      toast.success(res.data?.message || `Closed ${pos.symbol} position`);
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Close failed');
    } finally {
      setActionLoadingId(null);
    }
  };

  const openPositions = positions.filter(p => p.status === 'OPEN');
  const closedPositions = positions.filter(p => p.status !== 'OPEN');

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>Trading</Typography>
        <ToggleButtonGroup value={mode} exclusive onChange={(_, v) => v && setMode(v)} size="small">
          <ToggleButton value="PAPER" sx={{ fontWeight: 700 }}>Paper</ToggleButton>
          <ToggleButton value="LIVE" sx={{ fontWeight: 700, color: 'error.main' }}>Live</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {account && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Current Capital', value: fmt(account.current_capital) },
            { label: 'Realized P&L', value: fmt(account.realized_pnl), color: parseFloat(account.realized_pnl) >= 0 ? '#2e7d32' : '#c62828' },
            { label: 'Total Trades', value: account.total_trades },
            { label: 'Win Rate', value: account.total_trades > 0 ? `${((account.winning_trades / account.total_trades) * 100).toFixed(1)}%` : '0%' },
          ].map(s => (
            <Grid item xs={6} sm={3} key={s.label}>
              <Card sx={{ borderRadius: 2 }}><CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                <Typography variant="h6" fontWeight={700} sx={{ color: s.color }}>{s.value}</Typography>
              </CardContent></Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Card sx={{ borderRadius: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: '1px solid #e0e0e0' }}>
          <Tab label={`Open Positions (${openPositions.length})`} />
          <Tab label={`Closed Positions (${closedPositions.length})`} />
          <Tab label="Orders" />
        </Tabs>
        <CardContent>
          {tab === 0 && <PositionsTable positions={openPositions} loading={loading} showAction onClosePosition={handleClosePosition} actionLoadingId={actionLoadingId} />}
          {tab === 1 && <PositionsTable positions={closedPositions} loading={loading} />}
          {tab === 2 && (
            loading ? <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress /></Box> : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 600, bgcolor: '#f5f5f5' } }}>
                      <TableCell>Time</TableCell><TableCell>Type</TableCell>
                      <TableCell>Symbol</TableCell><TableCell align="right">Strike</TableCell>
                      <TableCell align="right">Qty</TableCell><TableCell align="right">Price</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orders.map(o => (
                      <TableRow key={o.id} hover>
                        <TableCell>{dayjs(o.created_at).format('DD/MM HH:mm')}</TableCell>
                        <TableCell><Chip size="small" label={o.order_type} color={o.order_type === 'BUY' ? 'success' : 'error'} /></TableCell>
                        <TableCell>{o.symbol} {o.instrument_type}</TableCell>
                        <TableCell align="right">{o.strike_price}</TableCell>
                        <TableCell align="right">{o.quantity}</TableCell>
                        <TableCell align="right">{fmt(o.executed_price || o.requested_price)}</TableCell>
                        <TableCell><Chip size="small" label={o.status} color={o.status === 'EXECUTED' ? 'success' : o.status === 'REJECTED' ? 'error' : 'default'} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
